import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const InternalApprovalModal = ({
  isOpen,
  onClose,
  onSuccess,
  onRejected,
  gym,
  targetStage = null, // If provided, use this stage; otherwise determine based on current stage
  initialAction = null // 'approve' | 'reject' — autoscroll/focus the matching section on open
}) => {
  const rejectionTextareaRef = useRef(null);
  const { user } = useAuth();
  const [step, setStep] = useState('review'); // review, approving, rejecting, success, rejected, error
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvalRemarks, setApprovalRemarks] = useState('');
  const [error, setError] = useState(null);

  // Determine the target stage based on current gym stage
  const getTargetStage = () => {
    if (targetStage) return targetStage;
    // Internal Approval -> CEO Approval (HoA / COO step)
    if (gym?.stage === 'Internal Approval') return 'CEO Approval';
    // CEO Approval -> Proposal Sent (CEO-only step)
    if (gym?.stage === 'CEO Approval') return 'Proposal Sent';
    // Default: move to Agreement Signed (legacy behavior)
    return 'Agreement Signed';
  };

  const isInternalApprovalStage = gym?.stage === 'Internal Approval';
  const isCEOApprovalStage = gym?.stage === 'CEO Approval';
  const isCEO = user?.role?.level === 1;
  const isCOO = user?.role?.level === 2;
  const isHoA = user?.role?.level === 3;
  // HoA, COO, and CEO can approve Internal Approval -> CEO Approval
  const canApproveInternalApproval = isCEO || isCOO || isHoA;
  // Only CEO can approve CEO Approval -> Proposal Sent
  const canApproveCEOApproval = isCEO;

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen && gym) {
      setStep('review');
      setRejectionReason('');
      setApprovalRemarks('');
      setError(null);

      // If opened via the card's Reject button, scroll the rejection section
      // into view and focus the textarea so the intent is obvious.
      if (initialAction === 'reject') {
        setTimeout(() => {
          const el = rejectionTextareaRef.current;
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.focus({ preventScroll: true });
          }
        }, 100);
      }
    }
  }, [isOpen, gym, initialAction]);

  const formatCurrency = (amount) => {
    if (!amount) return 'Not specified';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleApprove = async () => {
    if (!approvalRemarks.trim()) {
      setError('Please provide remarks for approval');
      return;
    }

    // Check permission for Internal Approval stage (HoA, COO, CEO can approve)
    if (isInternalApprovalStage && !canApproveInternalApproval) {
      setError('Only Head of Acquisition, COO, or CEO can approve gyms from Internal Approval stage');
      return;
    }

    // Check permission for CEO Approval stage (CEO only)
    if (isCEOApprovalStage && !canApproveCEOApproval) {
      setError('Only the CEO can approve gyms from CEO Approval stage');
      return;
    }

    try {
      setStep('approving');
      setError(null);

      const destinationStage = getTargetStage();

      // Move gym to the target stage
      const response = await axios.put(`/api/gyms/${gym.id}/stage`, {
        stage: destinationStage,
        comments: `Internal approval granted by ${user?.name || 'user'}. Remarks: ${approvalRemarks.trim()}`
      });

      setStep('success');

      // Notify parent after short delay
      setTimeout(() => {
        onSuccess({ gym: response.data.gym });
      }, 1500);

    } catch (error) {
      console.error('Error approving gym:', error);
      setError(error.response?.data?.error || 'Failed to approve');
      setStep('error');
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }

    try {
      setStep('rejecting');
      setError(null);

      // Call the internal rejection endpoint
      const response = await axios.post(`/api/gyms/${gym.id}/internal-rejection`, {
        rejectionReason: rejectionReason.trim(),
        rejectedBy: user?.id
      });

      setStep('rejected');

      // Notify parent after short delay
      setTimeout(() => {
        onRejected({
          expiredClient: response.data.expiredClient,
          originalGymId: gym.id
        });
      }, 1500);

    } catch (error) {
      console.error('Error rejecting gym:', error);
      setError(error.response?.data?.error || 'Failed to reject');
      setStep('error');
    }
  };

  const handleClose = () => {
    if (step === 'approving' || step === 'rejecting') return; // Don't close while processing
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className={`px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r ${
          isCEOApprovalStage ? 'from-pink-600 to-pink-700' : isInternalApprovalStage ? 'from-amber-600 to-amber-700' : 'from-purple-600 to-purple-700'
        }`}>
          <div className="text-white">
            <h2 className="text-xl font-semibold">
              {isCEOApprovalStage ? 'CEO Final Approval' : isInternalApprovalStage ? 'Internal Approval (HoA / COO)' : 'Internal Approval'}
            </h2>
            <p className={`text-sm ${isCEOApprovalStage ? 'text-pink-100' : isInternalApprovalStage ? 'text-amber-100' : 'text-purple-100'} mt-1`}>
              {gym?.name} {isCEOApprovalStage ? '• CEO Only' : isInternalApprovalStage ? '• HoA / COO' : ''}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-white hover:text-purple-200"
            disabled={step === 'approving' || step === 'rejecting'}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Review Step */}
          {step === 'review' && (
            <div className="space-y-6">
              {/* Info Banner */}
              <div className={`${isCEOApprovalStage ? 'bg-pink-50 border-pink-200' : isInternalApprovalStage ? 'bg-amber-50 border-amber-200' : 'bg-purple-50 border-purple-200'} border rounded-lg p-4`}>
                <div className="flex items-start">
                  <svg className={`w-6 h-6 ${isCEOApprovalStage ? 'text-pink-600' : isInternalApprovalStage ? 'text-amber-600' : 'text-purple-600'} mt-0.5`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="ml-3">
                    <h3 className={`text-sm font-semibold ${isCEOApprovalStage ? 'text-pink-800' : isInternalApprovalStage ? 'text-amber-800' : 'text-purple-800'}`}>
                      {isCEOApprovalStage ? 'CEO Final Approval Required' : isInternalApprovalStage ? 'Management Approval Required' : 'Internal Review Required'}
                    </h3>
                    <p className={`text-sm ${isCEOApprovalStage ? 'text-pink-700' : isInternalApprovalStage ? 'text-amber-700' : 'text-purple-700'} mt-1`}>
                      {isCEOApprovalStage
                        ? 'Only the CEO can approve this gym to move it to Proposal Sent. Review the details below.'
                        : isInternalApprovalStage
                          ? 'Head of Acquisition or COO can approve this gym. After approval it moves to CEO Approval for final sign-off.'
                          : 'Review the gym details below and decide whether to approve or reject this proposal.'
                      }
                    </p>
                  </div>
                </div>
              </div>

              {/* Permission Warning */}
              {isInternalApprovalStage && !canApproveInternalApproval && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <p className="text-sm text-red-700 font-medium">
                      You do not have permission to approve this gym. Only Head of Acquisition, COO, or CEO can approve the Internal Approval step.
                    </p>
                  </div>
                </div>
              )}

              {isCEOApprovalStage && !canApproveCEOApproval && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <p className="text-sm text-red-700 font-medium">
                      Only the CEO can approve a gym from CEO Approval to Proposal Sent.
                    </p>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Gym Details */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700">Gym Details</h4>
                </div>
                <div className="p-4 bg-white">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Gym Name:</span>
                      <p className="font-medium text-gray-900">{gym?.name}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Contact Person:</span>
                      <p className="font-medium text-gray-900">{gym?.contact_person || 'Not specified'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Location:</span>
                      <p className="font-medium text-gray-900">{gym?.location || gym?.address || 'Not specified'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">State:</span>
                      <p className="font-medium text-gray-900">{gym?.state || 'Not specified'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Phone:</span>
                      <p className="font-medium text-gray-900">{gym?.phone || gym?.ownerContact || 'Not specified'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Email:</span>
                      <p className="font-medium text-gray-900">{gym?.ownerEmail || gym?.email || 'Not specified'}</p>
                    </div>
                    {gym?.size && (
                      <div>
                        <span className="text-gray-500">Size:</span>
                        <p className="font-medium text-gray-900">{gym.size} sq. ft.</p>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-500">Assigned To:</span>
                      <p className="font-medium text-gray-900">{gym?.assignedUser?.name || 'Not assigned'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Proposal Details */}
              {(gym?.proposalAmount || gym?.closerPercentage) && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-700">Proposal Details</h4>
                  </div>
                  <div className="p-4 bg-white">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {gym?.proposalAmount && (
                        <div>
                          <span className="text-gray-500">Proposal Amount:</span>
                          <p className="font-medium text-green-600 text-lg">{formatCurrency(gym.proposalAmount)}</p>
                        </div>
                      )}
                      {gym?.closerPercentage && (
                        <div>
                          <span className="text-gray-500">Closer Percentage:</span>
                          <p className="font-medium text-gray-900">{gym.closerPercentage}%</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Existing Gym Remarks */}
              {gym?.remarks && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-700">Existing Remarks</h4>
                  </div>
                  <div className="p-4 bg-white">
                    <p className="text-sm text-gray-700">{gym.remarks}</p>
                  </div>
                </div>
              )}

              {/* Approval Remarks Input */}
              <div className="border border-green-200 rounded-lg overflow-hidden bg-green-50">
                <div className="bg-green-100 px-4 py-2 border-b border-green-200">
                  <h4 className="text-sm font-semibold text-green-700">Approval Remarks <span className="text-red-500">*</span></h4>
                </div>
                <div className="p-4">
                  <textarea
                    value={approvalRemarks}
                    onChange={(e) => setApprovalRemarks(e.target.value)}
                    placeholder="Enter remarks for approval (required)..."
                    rows={3}
                    className="w-full border border-green-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <p className="text-xs text-green-600 mt-1">
                    These remarks will be recorded with the approval
                  </p>
                </div>
              </div>

              {/* Rejection Reason Input */}
              <div className="border border-red-200 rounded-lg overflow-hidden bg-red-50">
                <div className="bg-red-100 px-4 py-2 border-b border-red-200">
                  <h4 className="text-sm font-semibold text-red-700">Rejection Reason (if rejecting)</h4>
                </div>
                <div className="p-4">
                  <textarea
                    ref={rejectionTextareaRef}
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Enter the reason for rejection (required if rejecting)..."
                    rows={3}
                    className="w-full border border-red-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                  <p className="text-xs text-red-600 mt-1">
                    This reason will be stored with the expired client record
                  </p>
                </div>
              </div>

              {/* Approver Info */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span className="text-sm text-gray-600">
                    Approving/Rejecting as: <strong className="text-gray-800">{user?.name}</strong> ({user?.role?.name})
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Approving Step */}
          {step === 'approving' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-200 border-t-purple-600 mb-6"></div>
              <h3 className="text-lg font-semibold text-gray-800">Approving...</h3>
              <p className="text-sm text-gray-500 mt-2">Moving gym to {getTargetStage()} stage</p>
            </div>
          )}

          {/* Rejecting Step */}
          {step === 'rejecting' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-red-200 border-t-red-600 mb-6"></div>
              <h3 className="text-lg font-semibold text-gray-800">Rejecting...</h3>
              <p className="text-sm text-gray-500 mt-2">Moving gym to Expired Clients</p>
            </div>
          )}

          {/* Success Step */}
          {step === 'success' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-6">
                <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-800">Approved!</h3>
              <p className="text-sm text-gray-500 mt-2 text-center">
                <strong>{gym?.name}</strong> has been moved to {getTargetStage()} stage
              </p>
            </div>
          )}

          {/* Rejected Step */}
          {step === 'rejected' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mb-6">
                <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-800">Rejected</h3>
              <p className="text-sm text-gray-500 mt-2 text-center">
                <strong>{gym?.name}</strong> has been moved to Expired Clients
              </p>
              <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 max-w-sm">
                <p className="text-xs text-red-600">
                  <strong>Reason:</strong> {rejectionReason}
                </p>
                <p className="text-xs text-red-600 mt-1">
                  <strong>Rejected by:</strong> {user?.name}
                </p>
              </div>
            </div>
          )}

          {/* Error Step */}
          {step === 'error' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mb-6">
                <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-800">Error</h3>
              <p className="text-sm text-red-600 mt-2 text-center">{error}</p>

              <button
                onClick={() => setStep('review')}
                className="mt-6 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                Try Again
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3">
          {step === 'review' && (
            <>
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectionReason.trim()}
                className="px-6 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Reject
              </button>
              <button
                onClick={handleApprove}
                disabled={!approvalRemarks.trim() || (isInternalApprovalStage && !canApproveInternalApproval) || (isCEOApprovalStage && !canApproveCEOApproval)}
                className="px-6 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Approve
              </button>
            </>
          )}

          {(step === 'success' || step === 'rejected') && (
            <button
              onClick={handleClose}
              className="px-6 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700"
            >
              Done
            </button>
          )}

          {step === 'error' && (
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default InternalApprovalModal;
